import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import {
  MappingRow,
  MappingTabConfig,
  UnassignedMappingRow
} from './client-data-mapping-constants';
import { useClientTargetPicker } from './ClientTargetPicker/ClientTargetPicker';

interface CompanyPickerModalProps {
  tab: MappingTabConfig;
  // The row being acted on. Presence of `cid` distinguishes a reassignment (MappingRow,
  // currently owned by cid) from an assignment (UnassignedMappingRow, never owned) -- callers
  // should only render this modal when they have a row to act on (conditional render at the
  // call site), so the parent guarantees non-null here.
  row: MappingRow | UnassignedMappingRow;
  onHide: () => void;
  // Fire-and-forget: the modal hands back the user's picked target cids and closes; the
  // parent wraps the actual assign / reassign API call in optimisticMutate so the row delta
  // applies before the network round-trip resolves. See ClientDataMapping.handleAssignSubmit
  // / handleReassignSubmit.
  onSubmit: (targetCids: string[]) => void;
}

const isReassignRow = (row: MappingRow | UnassignedMappingRow): row is MappingRow => 'cid' in row;

export const CompanyPickerModal = ({ tab, row, onHide, onSubmit }: CompanyPickerModalProps) => {
  const fromCid = isReassignRow(row) ? row.cid : undefined;
  const { displayName } = row;

  // Shared client + generic picker -- same UI + state as AddMappingModal. Excludes the
  // reassign source cid (if any) from the dropdown options so "reassign to yourself" isn't
  // offered. `showOrWrapper: false` drops the outlined OR box that AddMappingModal uses --
  // this modal is already scoped to a single action, so the additional visual grouping is
  // redundant here.
  const target = useClientTargetPicker({
    tab,
    excludeCids: fromCid ? [fromCid] : [],
    checkboxId: 'company-picker-mark-generic',
    showOrWrapper: false,
    // Assign/reassign flows target a specific client -- marking generic is a separate,
    // explicit action (globe button on the row). Showing the checkbox here would be confusing.
    hideMarkGeneric: true
  });

  const handleSave = () => {
    if (!target.isValid) return;
    onSubmit(target.targetCids);
    onHide();
  };

  const submitLabel = fromCid ? 'Reassign' : 'Assign';

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {fromCid ? 'Reassign' : 'Assign'} &ldquo;{displayName}&rdquo;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{target.render()}</Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!target.isValid} onClick={handleSave}>
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
